import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { PawPrint, ChevronRight, CheckCircle2, ArrowLeft } from "lucide-react";

const STEPS = ["mobile", "otp", "details", "service", "slot", "confirm", "done"];

export default function PublicBooking() {
  const [step, setStep] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpToken, setOtpToken] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [pets, setPets] = useState([]);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newPet, setNewPet] = useState({ name: "", species: "dog", breed: "", gender: "unknown" });
  const [selectedPetId, setSelectedPetId] = useState(null);
  const [usingNewPet, setUsingNewPet] = useState(false);
  const [services, setServices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [serviceId, setServiceId] = useState(null);
  const [slotDate, setSlotDate] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [slotTime, setSlotTime] = useState("11:00");
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/branches`).then(r => {
      setBranches(r.data);
      if (r.data.length > 0) setBranchId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!branchId) return;
    axios.get(`${API}/public/services`, { params: { branch_id: branchId } }).then(r => setServices(r.data));
  }, [branchId]);

  const requestOtp = async () => {
    if (!/^[0-9]{10,15}$/.test(mobile.replace(/\D/g, ""))) { toast.error("Enter a valid mobile number"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/otp/request`, { mobile });
      setOtpToken(data.token);
      toast.success("OTP sent to your WhatsApp");
      setStep("otp");
    } catch (e) { toast.error("Failed to send OTP"); } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/otp/verify`, { mobile, code: otp });
      setOtpToken(data.token);
      // Fetch customer
      const cust = await axios.get(`${API}/public/customer-by-mobile`, { params: { mobile, token: data.token } });
      if (cust.data.exists) {
        setCustomer(cust.data.customer);
        setPets(cust.data.pets);
        if (cust.data.pets.length > 0) setSelectedPetId(cust.data.pets[0].id);
        else setUsingNewPet(true);
      }
      setStep("details");
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid OTP"); } finally { setBusy(false); }
  };

  const detailsNext = () => {
    if (!customer && !newCustomerName) { toast.error("Please enter your name"); return; }
    if (!selectedPetId && (!newPet.name || !newPet.species)) { toast.error("Please add pet details"); return; }
    setStep("service");
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        mobile,
        otp_token: otpToken,
        customer_name: customer ? null : newCustomerName,
        pet_id: usingNewPet ? null : selectedPetId,
        new_pet: usingNewPet ? { name: newPet.name, species: newPet.species, breed: newPet.breed || null, gender: newPet.gender } : null,
        service_id: serviceId,
        slot_start: `${slotDate}T${slotTime}:00`,
        branch_id: branchId,
        notes,
      };
      const { data } = await axios.post(`${API}/public/book`, payload);
      setResult(data);
      setStep("done");
    } catch (e) { toast.error(e?.response?.data?.detail || "Booking failed"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 bg-card/70 backdrop-blur-xl border-b border-white/40">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground grid place-items-center"><PawPrint className="w-4 h-4" /></div>
            <div>
              <div className="font-heading font-bold text-lg leading-none">Furmigo</div>
              <div className="text-[10px] uppercase tracking-[0.2em] mt-1 text-muted-foreground">Pet care, booked.</div>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">Step {STEPS.indexOf(step) + 1} of {STEPS.length - 1}</Badge>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6 sm:p-12">
        {step === "mobile" && (
          <div className="animate-fade-in">
            <h1 className="font-heading text-4xl sm:text-6xl font-black tracking-tight leading-[1.05]">Book a fuss-free visit for your pet.</h1>
            <p className="text-muted-foreground mt-4 text-lg">Grooming, vet, training & more — confirmed on WhatsApp in under a minute.</p>
            <Card className="mt-10 p-8">
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Your mobile number</Label>
              <Input value={mobile} onChange={e => setMobile(e.target.value)} placeholder="e.g. 9876543210" className="h-14 text-lg mt-2" autoFocus data-testid="pb-mobile" />
              <Button className="mt-4 w-full h-12" onClick={requestOtp} disabled={busy} data-testid="pb-send-otp">
                {busy ? "Sending OTP…" : "Send OTP via WhatsApp"} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Card>
            <div className="mt-8 grid sm:grid-cols-3 gap-3">
              {[
                { t: "Grooming", url: "https://images.unsplash.com/photo-1581562324420-eff2f5aaa4b5?crop=entropy&cs=srgb&fm=jpg&q=80&w=400" },
                { t: "Vet visits", url: "https://images.unsplash.com/photo-1725409796872-8b41e8eca929?crop=entropy&cs=srgb&fm=jpg&q=80&w=400" },
                { t: "Boarding & more", url: "https://images.unsplash.com/photo-1604508558335-949a7daeeddd?crop=entropy&cs=srgb&fm=jpg&q=80&w=400" },
              ].map(card => (
                <div key={card.t} className="rounded-xl overflow-hidden border border-border bg-card">
                  <img src={card.url} alt={card.t} className="w-full h-32 object-cover" />
                  <div className="p-3 text-sm font-semibold">{card.t}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === "otp" && (
          <Card className="p-8 animate-fade-in">
            <Button variant="ghost" size="sm" onClick={() => setStep("mobile")}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
            <h2 className="font-heading text-3xl font-bold tracking-tight mt-3">Enter the OTP</h2>
            <p className="text-muted-foreground mt-1">Sent to <span className="font-mono">{mobile}</span> via WhatsApp.</p>
            <Input value={otp} onChange={e => setOtp(e.target.value)} placeholder="6-digit code" maxLength={6} className="h-14 mt-4 text-center text-2xl tracking-[0.5em] font-mono" data-testid="pb-otp" />
            <Button className="w-full mt-4 h-12" onClick={verifyOtp} disabled={busy || otp.length < 4} data-testid="pb-verify-otp">
              {busy ? "Verifying…" : "Verify & continue"}
            </Button>
          </Card>
        )}

        {step === "details" && (
          <Card className="p-8 animate-fade-in space-y-5">
            <Button variant="ghost" size="sm" onClick={() => setStep("otp")}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Tell us about you & your pet</h2>
            {!customer ? (
              <div>
                <Label>Your name</Label>
                <Input value={newCustomerName} onChange={e => setNewCustomerName(e.target.value)} placeholder="Full name" className="h-12 mt-1" data-testid="pb-cust-name" />
              </div>
            ) : (
              <div className="rounded-md bg-muted px-4 py-3 text-sm">Welcome back, <span className="font-semibold">{customer.name}</span>!</div>
            )}

            {pets.length > 0 && !usingNewPet && (
              <div>
                <Label>Choose your pet</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {pets.map(p => (
                    <button key={p.id} onClick={() => setSelectedPetId(p.id)} className={`rounded-lg border p-3 text-left ${selectedPetId === p.id ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`pb-pet-${p.id}`}>
                      <div className="font-heading font-bold">{p.name}</div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider">{p.species} · {p.breed || "—"}</div>
                    </button>
                  ))}
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedPetId(null); setUsingNewPet(true); }} className="mt-2" data-testid="pb-new-pet">+ Add a new pet</Button>
              </div>
            )}

            {usingNewPet && (
              <div className="border-t border-border pt-4">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">New pet</div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Pet name</Label><Input value={newPet.name} onChange={e => setNewPet(p => ({ ...p, name: e.target.value }))} data-testid="pb-newpet-name" /></div>
                  <div><Label>Species</Label>
                    <Select value={newPet.species} onValueChange={v => setNewPet(p => ({ ...p, species: v }))}>
                      <SelectTrigger data-testid="pb-newpet-species"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dog">Dog</SelectItem>
                        <SelectItem value="cat">Cat</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Breed</Label><Input value={newPet.breed} onChange={e => setNewPet(p => ({ ...p, breed: e.target.value }))} /></div>
                  <div><Label>Gender</Label>
                    <Select value={newPet.gender} onValueChange={v => setNewPet(p => ({ ...p, gender: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="unknown">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {pets.length > 0 && <Button variant="ghost" size="sm" onClick={() => setUsingNewPet(false)} className="mt-2">← Pick an existing pet</Button>}
              </div>
            )}

            <Button className="w-full h-12" onClick={detailsNext} data-testid="pb-details-next">Continue <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </Card>
        )}

        {step === "service" && (
          <Card className="p-8 animate-fade-in space-y-4">
            <Button variant="ghost" size="sm" onClick={() => setStep("details")}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Pick a service</h2>
            {branches.length > 1 && (
              <div>
                <Label>Branch</Label>
                <Select value={branchId || ""} onValueChange={setBranchId}>
                  <SelectTrigger className="h-11 mt-1" data-testid="pb-branch"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}{b.address ? ` — ${b.address}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              {services.map(s => (
                <button
                  key={s.id}
                  onClick={() => setServiceId(s.id)}
                  data-testid={`pb-svc-${s.id}`}
                  className={`text-left rounded-xl border p-4 hover:-translate-y-0.5 transition-all ${serviceId === s.id ? "border-primary bg-primary/5" : "border-border"}`}
                >
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.category} · {s.duration_min}m</div>
                  <div className="font-heading font-bold text-lg mt-1">{s.name}</div>
                  <div className="mt-2 font-mono text-sm">₹{s.price}</div>
                </button>
              ))}
            </div>
            <Button className="w-full h-12" disabled={!serviceId} onClick={() => setStep("slot")} data-testid="pb-svc-next">Continue <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </Card>
        )}

        {step === "slot" && (
          <Card className="p-8 animate-fade-in space-y-4">
            <Button variant="ghost" size="sm" onClick={() => setStep("service")}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Pick a slot</h2>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={slotDate} onChange={e => setSlotDate(e.target.value)} className="h-12" data-testid="pb-date" /></div>
              <div><Label>Time</Label><Input type="time" value={slotTime} onChange={e => setSlotTime(e.target.value)} className="h-12" data-testid="pb-time" /></div>
            </div>
            <div><Label>Notes (optional)</Label><Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Special requests" /></div>
            <Button className="w-full h-12" onClick={() => setStep("confirm")} data-testid="pb-slot-next">Review <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </Card>
        )}

        {step === "confirm" && (
          <Card className="p-8 animate-fade-in space-y-4">
            <Button variant="ghost" size="sm" onClick={() => setStep("slot")}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Review & confirm</h2>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Mobile" v={mobile} />
              <Info label="Name" v={customer ? customer.name : newCustomerName} />
              <Info label="Pet" v={usingNewPet ? `${newPet.name} (${newPet.species})` : pets.find(p => p.id === selectedPetId)?.name} />
              <Info label="Service" v={services.find(s => s.id === serviceId)?.name} />
              <Info label="When" v={`${slotDate} ${slotTime}`} />
            </div>
            <Button className="w-full h-14 text-lg" disabled={busy} onClick={submit} data-testid="pb-confirm">
              {busy ? "Booking…" : "Confirm booking"}
            </Button>
          </Card>
        )}

        {step === "done" && result && (
          <Card className="p-10 text-center animate-fade-in">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" />
            <h2 className="font-heading text-3xl font-bold tracking-tight mt-4">Booking confirmed!</h2>
            <p className="text-muted-foreground mt-2">We've sent a confirmation to your WhatsApp.</p>
            <div className="mt-6 rounded-xl bg-muted p-4 inline-block text-left">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Service</div>
              <div className="font-heading font-bold text-lg">{result.service}</div>
              <div className="text-sm mt-2"><span className="text-muted-foreground">Booking ID:</span> <span className="font-mono">{result.appointment_id.slice(0, 8)}</span></div>
            </div>
          </Card>
        )}
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}

const Info = ({ label, v }) => (
  <div className="rounded-md border border-border p-3">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="font-medium mt-1">{v || "—"}</div>
  </div>
);
